"use client";
import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import { Search, XMarkThin } from "@/icons";
import { onSearchSubmit } from "../Navigation.helpers";
import { GlobalNavigationProps } from "..";

type SearchBarProps = {
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
} & Pick<NonNullable<GlobalNavigationProps["superHeader"]>, "search">;

const SearchBar: React.FC<SearchBarProps> = ({
  search,
  isSearchOpen,
  setIsSearchOpen,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const iconBtnStyles =
    "focus-visible:outline-swdc-blue-400 group cursor-pointer rounded outline-none focus-visible:outline-2 focus-visible:-outline-offset-2";
  const iconSvgStyles =
    "[&_path]:fill-white group-hover:[&_path]:fill-swdc-gray-200 group-focus-visible:[&_path]:fill-swdc-gray-200";

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <li
      className={cn("relative min-h-6", isSearchOpen ? "min-w-194" : "min-w-5")}
    >
      {isSearchOpen && (
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            onSearchSubmit(searchValue);
          }}
          className="relative flex h-6 w-full justify-center rounded-full bg-transparent text-white"
          role="search"
        >
          <label className="sr-only" htmlFor="search-input">
            {search?.inputLabel}
          </label>
          <input
            className="text-body-s w-full rounded-full border-1 border-white bg-transparent pr-16 pl-4 text-white placeholder:text-white focus:border-black focus:outline focus:outline-white"
            id="search-input"
            type="text"
            placeholder={search?.inputPlaceholder}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            ref={searchInputRef}
            {...getDataAutomationGroup("input-field")}
          />
          <div className="absolute inset-y-0 right-3 mx-auto flex justify-center">
            <button
              className={cn(iconBtnStyles, "mx-auto px-1")}
              type="submit"
              {...getDataLinkAttributes({
                dld: search?.submitButtonDLD,
                dlt: search?.submitButtonDLT,
              })}
              {...getDataAutomationId("search-icon")}
            >
              <span className="sr-only">{search?.submitButtonLabel}</span>
              <Search
                type="icon-20"
                width={20}
                height={20}
                className={iconSvgStyles}
              />
            </button>
            <button
              className={cn(iconBtnStyles, "px-1")}
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
              }}
              {...getDataLinkAttributes({
                dld: search?.hideButtonDLD,
                dlt: search?.hideButtonDLT,
              })}
              {...getDataAutomationId("close-icon")}
            >
              <span className="sr-only">{search?.hideButtonLabel}</span>
              <XMarkThin className={cn(iconSvgStyles, "size-6")} />
            </button>
          </div>
        </form>
      )}
      {!isSearchOpen && (
        <button
          className={cn(
            iconBtnStyles,
            "absolute top-0.5 bottom-0 flex h-fit cursor-pointer items-center justify-center outline-offset-2!",
          )}
          onClick={() => {
            setIsSearchOpen(true);
          }}
          {...getDataLinkAttributes({
            dld: search?.showButtonDLD,
            dlt: search?.showButtonDLT,
          })}
          {...getDataAutomationId("search-icon")}
        >
          <span className="sr-only">{search?.showButtonLabel}</span>
          <Search
            type="icon-20"
            width={20}
            height={20}
            className={iconSvgStyles}
          />
        </button>
      )}
    </li>
  );
};

export default SearchBar;
